import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video, FileText, Camera, FileBarChart, Share2, AlertTriangle, Calendar, ExternalLink, CheckCircle2, ArrowLeft,
  CalendarCheck, ThumbsUp, MessageSquareWarning, X,
} from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Demanda, StatusDemanda, Area } from '@/lib/types';

type Bloco = 'Vídeos' | 'Copy' | 'Fotos' | 'Relatórios' | 'Redes' | 'Outros';

const BLOCOS: { key: Bloco; label: string; icon: typeof Video }[] = [
  { key: 'Vídeos',     label: 'Vídeos',     icon: Video },
  { key: 'Copy',       label: 'Copy',       icon: FileText },
  { key: 'Fotos',      label: 'Fotos',      icon: Camera },
  { key: 'Relatórios', label: 'Relatórios', icon: FileBarChart },
  { key: 'Redes',      label: 'Redes',      icon: Share2 },
  { key: 'Outros',     label: 'Outros',     icon: CheckCircle2 },
];

const STATUS_PILL: Record<StatusDemanda, { label: string; cls: string }> = {
  'Triagem':              { label: 'Briefing',    cls: 'bg-blue/15 text-blue-light border-blue/30' },
  'Em produção':          { label: 'Em produção', cls: 'bg-orange/15 text-orange border-orange/30' },
  'Em revisão':           { label: 'Em revisão',  cls: 'bg-purple/15 text-purple border-purple/30' },
  'Aguardando aprovação': { label: 'Aprove você', cls: 'bg-pink/15 text-pink border-pink/30' },
  'Publicada':            { label: 'Finalizado',   cls: 'bg-green/15 text-green border-green/30' },
  'Arquivada':            { label: 'Arquivada',   cls: 'bg-muted/15 text-muted border-muted/30' },
  'Cancelada':            { label: 'Cancelada',   cls: 'bg-red/15 text-red border-red/30' },
};

function inferirBloco(areasNomes: string[], titulo: string, pauta?: string): Bloco {
  const corpo = (titulo + ' ' + (pauta ?? '') + ' ' + areasNomes.join(' ')).toLowerCase();
  if (/(instagram|linkedin|facebook|stories|post |reels?|tiktok|x\.com|twitter|threads)/i.test(corpo)) return 'Redes';
  if (/(relat[oó]rio|setorial|carta do econ|carta econ|an[aá]lise|estudo|dossiê|whitepaper)/i.test(corpo)) return 'Relatórios';
  if (/(produtora|v[ií]deo|reels?|youtube|tv|stream)/i.test(corpo)) return 'Vídeos';
  if (/(foto|imagem|design|branding|arte)/i.test(corpo)) return 'Fotos';
  if (/(conte[uú]do|copy|texto|reda[cç][aã]o|redator|editorial|coluna|matéria|materia|news)/i.test(corpo)) return 'Copy';
  return 'Outros';
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function PortalCliente() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [blocoAtivo, setBlocoAtivo] = useState<Bloco>('Vídeos');

  const asSolicitanteId = params.get('as');

  const demandasQ = useQuery({ queryKey: ['demandas', 'cliente'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const contratosQ = useQuery({ queryKey: ['contratos', 'cliente'], queryFn: () => api.contratos.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'], queryFn: () => api.areas.list().then((r) => r.data) });
  const eventosQ = useQuery({ queryKey: ['eventos-portal'], queryFn: () => api.eventos.listPortal().then((r) => r.data) });
  const inscricoesQ = useQuery({ queryKey: ['inscricoes-portal'], queryFn: () => api.inscricoes.list().then((r) => r.data) });
  const qc = useQueryClient();
  const inscrever = useMutation({
    mutationFn: (eventoId: string) => api.inscricoes.create({ eventoId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inscricoes-portal'] }),
  });
  const aprovarDemanda = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario?: string }) => api.demandas.aprovar(id, comentario),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['demandas', 'cliente'] }),
  });
  const pedirRevisaoDemanda = useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) => api.demandas.pedirRevisao(id, feedback),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['demandas', 'cliente'] }),
  });
  const [aprovacaoAberta, setAprovacaoAberta] = useState<Demanda | null>(null);
  const [revisaoAberta, setRevisaoAberta] = useState<Demanda | null>(null);

  const allDemandas = demandasQ.data ?? [];
  const eventosAbertos = (eventosQ.data ?? []).filter((e) => e.inscricoesAbertas).sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''));
  const allContratos = contratosQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const areas = areasQ.data ?? [];

  const targetSolicitanteId = asSolicitanteId ?? user?.solicitanteId ?? null;

  const filtrarPorTarget = user?.papel !== 'Cliente' && targetSolicitanteId;
  const demandas = filtrarPorTarget
    ? allDemandas.filter(
        (d) =>
          d.solicitanteId === targetSolicitanteId ||
          (d.clientesIds ?? []).includes(targetSolicitanteId!),
      )
    : allDemandas;
  const contratos = filtrarPorTarget
    ? allContratos.filter((c) => c.solicitanteId === targetSolicitanteId)
    : allContratos;

  const meuSolicitante = useMemo(() => solicitantes.find((s) => s.id === targetSolicitanteId), [solicitantes, targetSolicitanteId]);
  const meuContrato = useMemo(() => contratos.find((c) => c.status === 'Vigente') ?? contratos[0], [contratos]);

  const areaMap = useMemo(() => new Map<string, Area>(areas.map((a) => [a.id, a])), [areas]);

  const blocoPorDemanda = useMemo(() => {
    const m = new Map<string, Bloco>();
    for (const d of demandas) {
      const nomes = d.areasIds.map((id) => areaMap.get(id)?.nome ?? '').filter(Boolean);
      m.set(d.id, inferirBloco(nomes, d.titulo, d.pauta));
    }
    return m;
  }, [demandas, areaMap]);

  const contagemPorBloco = useMemo(() => {
    const c: Record<Bloco, number> = { 'Vídeos': 0, 'Copy': 0, 'Fotos': 0, 'Relatórios': 0, 'Redes': 0, 'Outros': 0 };
    for (const d of demandas) {
      if (d.status !== 'Publicada') continue;
      const b = blocoPorDemanda.get(d.id) ?? 'Outros';
      c[b]++;
    }
    return c;
  }, [demandas, blocoPorDemanda]);

  const publicadas = demandas.filter((d) => d.status === 'Publicada');
  const emProducao = demandas.filter((d) => ['Triagem', 'Em produção', 'Em revisão'].includes(d.status));
  const aguardandoVoce = demandas.filter((d) => d.status === 'Aguardando aprovação');

  const filtradas = useMemo(() => {
    return publicadas
      .filter((d) => (blocoPorDemanda.get(d.id) ?? 'Outros') === blocoAtivo)
      .sort((a, b) => (b.dataPublicacao ?? '').localeCompare(a.dataPublicacao ?? ''));
  }, [publicadas, blocoPorDemanda, blocoAtivo]);

  const heroTitle = meuSolicitante?.nome ?? user?.nome ?? 'Portal';
  const verificandoComoCliente = !!asSolicitanteId && user?.papel !== 'Cliente';

  return (
    <div>
      <PageHeader
        title={heroTitle}
        action={
          verificandoComoCliente ? (
            <Link to="/portais" className="btn-secondary !py-1.5 !px-3 text-xs">
              <ArrowLeft className="w-3.5 h-3.5" /> Trocar cliente
            </Link>
          ) : undefined
        }
      />

      {verificandoComoCliente && (
        <div className="px-6 py-2 bg-blue/10 border-b border-blue/30 text-[11px] text-blue-light">
          Visualizando como cliente — só leitura. Tudo que aparece aqui é o que {meuSolicitante?.nome ?? 'ele'} vê.
        </div>
      )}

      <div className="px-6 py-4 space-y-4">
        {aguardandoVoce.length > 0 && (
          <div className="card !p-3 border-pink/30 bg-pink/5">
            <div className="flex items-center gap-2 mb-2.5">
              <AlertTriangle className="w-4 h-4 text-pink shrink-0" />
              <h3 className="text-xs font-bold">
                {aguardandoVoce.length} {aguardandoVoce.length === 1 ? 'peça aguardando' : 'peças aguardando'} sua aprovação
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {aguardandoVoce.map((d) => (
                <div key={d.id} className="bg-panel border border-pink/20 rounded-md p-2.5">
                  <div className="font-semibold text-text text-xs leading-snug line-clamp-2 mb-1">{d.titulo}</div>
                  {d.pauta && <p className="text-[11px] text-text-2 mb-2 line-clamp-2">{d.pauta}</p>}
                  {d.linkFinal && (
                    <a href={d.linkFinal} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mb-2">
                      Ver peça <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      type="button"
                      onClick={() => setAprovacaoAberta(d)}
                      disabled={aprovarDemanda.isPending}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-white bg-green hover:bg-green/90 rounded transition-colors disabled:opacity-50"
                    >
                      <ThumbsUp className="w-3 h-3" /> Aprovar
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevisaoAberta(d)}
                      disabled={pedirRevisaoDemanda.isPending}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-purple bg-purple/10 border border-purple/30 hover:bg-purple/20 rounded transition-colors disabled:opacity-50"
                    >
                      <MessageSquareWarning className="w-3 h-3" /> Pedir ajustes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Finalizado" color="green" value={publicadas.length}
            delta="entregas no histórico" icon={<Video className="w-[16px] h-[16px]" />} />
          <KpiCard label="Em produção" color="orange" value={emProducao.length}
            delta="próximas entregas" icon={<CheckCircle2 className="w-[16px] h-[16px]" />} />
          <KpiCard label="Pra aprovar" color="purple" value={aguardandoVoce.length}
            delta={aguardandoVoce.length ? 'aguardando OK' : 'nenhuma pendente'}
            icon={<AlertTriangle className="w-[16px] h-[16px]" />} />
          <KpiCard label="Cota" color="blue"
            value={meuContrato?.cotaFamilia ?? '—'}
            delta={meuContrato?.cotaHierarquia ?? 'sem contrato vigente'}
            icon={<Video className="w-[16px] h-[16px]" />} />
        </div>

        {eventosAbertos.length > 0 && (
          <div className="card !p-3">
            <div className="flex items-center gap-2 mb-2">
              <CalendarCheck className="w-4 h-4 text-blue-light" />
              <h3 className="text-xs font-bold">Próximos eventos com inscrições abertas</h3>
              <span className="text-[10px] text-muted">{eventosAbertos.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {eventosAbertos.slice(0, 6).map((ev) => {
                const jaInscrito = (inscricoesQ.data ?? []).some((i) => i.eventoId === ev.id);
                return (
                  <div key={ev.id} className="bg-panel-2 border border-border rounded-md p-2.5 text-xs">
                    <div className="font-semibold text-text leading-snug line-clamp-2 mb-1">{ev.nome}</div>
                    <div className="flex items-center justify-between text-[10px] text-muted mb-2">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-2.5 h-2.5" />
                        {ev.dataInicio ? new Date(ev.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </span>
                      {ev.local && <span className="truncate ml-2 max-w-[55%]">{ev.local}</span>}
                    </div>
                    {jaInscrito ? (
                      <div className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold text-green bg-green/10 border border-green/30 rounded">
                        ✓ Inscrito
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => inscrever.mutate(ev.id)}
                        disabled={inscrever.isPending}
                        className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold text-blue-light bg-blue/10 border border-blue/30 hover:bg-blue/15 rounded transition-colors disabled:opacity-50"
                      >
                        {inscrever.isPending ? 'Inscrevendo…' : 'Quero participar'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
          {BLOCOS.map(({ key, label, icon: Icon }) => {
            const active = blocoAtivo === key;
            const total = contagemPorBloco[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setBlocoAtivo(key)}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap',
                  active ? 'text-blue-light border-blue' : 'text-text-2 border-transparent hover:text-text',
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span className={clsx('text-[10px] font-medium ml-1 px-1.5 py-0.5 rounded',
                  active ? 'bg-blue/20 text-blue-light' : 'bg-panel-2 text-muted')}>{total}</span>
              </button>
            );
          })}
        </div>

        {demandasQ.isLoading && <div className="card text-sm text-muted">Carregando entregas…</div>}

        {!demandasQ.isLoading && filtradas.length === 0 && (
          <div className="card text-center py-10">
            <Video className="w-8 h-8 text-muted mx-auto mb-2" />
            <p className="text-sm text-muted">Sem entregas publicadas em {blocoAtivo}.</p>
          </div>
        )}

        {!demandasQ.isLoading && filtradas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtradas.map((d) => <PecaCard key={d.id} d={d} />)}
          </div>
        )}
      </div>

      {aprovacaoAberta && (
        <AprovacaoModal
          demanda={aprovacaoAberta}
          loading={aprovarDemanda.isPending}
          onClose={() => setAprovacaoAberta(null)}
          onConfirm={(comentario) => {
            aprovarDemanda.mutate(
              { id: aprovacaoAberta.id, comentario },
              { onSuccess: () => setAprovacaoAberta(null) },
            );
          }}
        />
      )}
      {revisaoAberta && (
        <RevisaoModal
          demanda={revisaoAberta}
          loading={pedirRevisaoDemanda.isPending}
          onClose={() => setRevisaoAberta(null)}
          onConfirm={(feedback) => {
            pedirRevisaoDemanda.mutate(
              { id: revisaoAberta.id, feedback },
              { onSuccess: () => setRevisaoAberta(null) },
            );
          }}
        />
      )}
    </div>
  );
}

function AprovacaoModal({ demanda, loading, onClose, onConfirm }: {
  demanda: Demanda;
  loading: boolean;
  onClose: () => void;
  onConfirm: (comentario?: string) => void;
}) {
  const [comentario, setComentario] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-panel border border-border rounded-lg w-full max-w-md shadow-2xl">
        <div className="flex items-start justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-bold text-text">Aprovar peça</h3>
            <p className="text-[11px] text-text-2 mt-0.5 line-clamp-2">{demanda.titulo}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-green/10 border border-green/30 rounded p-2.5 text-[11px] text-text-2">
            Ao confirmar, a peça vai pra <strong className="text-green">Finalizado</strong> e a coordenação é notificada.
          </div>
          <label className="block text-[11px] font-semibold text-text-2">Comentário (opcional)</label>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            className="w-full bg-panel-2 border border-border rounded px-2 py-1.5 text-xs text-text focus:outline-none focus:border-blue"
            placeholder="Algum elogio, observação ou pedido pra próxima leva..."
          />
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} disabled={loading}
            className="px-3 py-1.5 text-xs text-text-2 hover:text-text">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(comentario.trim() || undefined)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green hover:bg-green/90 rounded disabled:opacity-50"
          >
            <ThumbsUp className="w-3 h-3" />
            {loading ? 'Aprovando…' : 'Confirmar aprovação'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RevisaoModal({ demanda, loading, onClose, onConfirm }: {
  demanda: Demanda;
  loading: boolean;
  onClose: () => void;
  onConfirm: (feedback: string) => void;
}) {
  const [feedback, setFeedback] = useState('');
  const valid = feedback.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-panel border border-border rounded-lg w-full max-w-md shadow-2xl">
        <div className="flex items-start justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-bold text-text">Pedir ajustes</h3>
            <p className="text-[11px] text-text-2 mt-0.5 line-clamp-2">{demanda.titulo}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-purple/10 border border-purple/30 rounded p-2.5 text-[11px] text-text-2">
            Conta o que precisa mudar. A peça volta pra <strong className="text-purple">Em revisão</strong> e a coordenação recebe seu feedback por email.
          </div>
          <label className="block text-[11px] font-semibold text-text-2">O que ajustar? <span className="text-red">*</span></label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={5}
            autoFocus
            className="w-full bg-panel-2 border border-border rounded px-2 py-1.5 text-xs text-text focus:outline-none focus:border-purple"
            placeholder="Ex: troca a foto de capa, corta o último parágrafo, ajusta o tom do CTA..."
          />
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} disabled={loading}
            className="px-3 py-1.5 text-xs text-text-2 hover:text-text">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => valid && onConfirm(feedback.trim())}
            disabled={loading || !valid}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple bg-purple/15 border border-purple/40 hover:bg-purple/25 rounded disabled:opacity-50"
          >
            <MessageSquareWarning className="w-3 h-3" />
            {loading ? 'Enviando…' : 'Enviar pedido de ajuste'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PecaCard({ d }: { d: Demanda }) {
  const pill = STATUS_PILL[d.status] ?? STATUS_PILL['Triagem'];
  return (
    <div className="bg-panel border border-border rounded-lg p-3 hover:border-border-light transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h3 className="text-xs font-bold text-text leading-snug line-clamp-2">{d.titulo || '(sem título)'}</h3>
        <span className={clsx('shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border', pill.cls)}>
          {pill.label}
        </span>
      </div>
      {d.pauta && <p className="text-[11px] text-text-2 mb-2 line-clamp-2">{d.pauta}</p>}
      <div className="flex items-center justify-between text-[10px] text-muted">
        <span className="inline-flex items-center gap-1">
          <Calendar className="w-2.5 h-2.5" />
          {d.dataPublicacao ? formatDate(d.dataPublicacao) : (d.prazoSla ? `prazo ${formatDate(d.prazoSla)}` : 'sem data')}
        </span>
        {d.linkFinal && (
          <a href={d.linkFinal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-light hover:underline">
            ver <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
    </div>
  );
}
